import { useState, useEffect } from "react";
import { api } from "@/App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { PiggyBank, Plus, Edit2, Trash2, DollarSign, Calendar, Users } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";

const formatCurrency = (value) => {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
};

export default function Retirement() {
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContribution, setEditingContribution] = useState(null);
  
  // Form state
  const [formPerson, setFormPerson] = useState("");
  const [formContribution, setFormContribution] = useState("");
  const [formFrequency, setFormFrequency] = useState("biweekly");
  const [formEffectiveDate, setFormEffectiveDate] = useState(`${currentYear}-01-01`);
  const [formHasMatching, setFormHasMatching] = useState(false);
  const [formMatchingAmount, setFormMatchingAmount] = useState("");
  const [formMatchingEffectiveDate, setFormMatchingEffectiveDate] = useState("");

  // Update form effective date when year changes
  useEffect(() => {
    setFormEffectiveDate(`${year}-01-01`);
  }, [year]);

  useEffect(() => {
    fetchData();
  }, [year]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/retirement-contributions?year=${year}`);
      setSummary(res.data);
    } catch (error) {
      toast.error("Failed to load retirement data");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormPerson("");
    setFormContribution("");
    setFormFrequency("biweekly");
    setFormEffectiveDate(`${year}-01-01`);
    setFormHasMatching(false);
    setFormMatchingAmount("");
    setFormMatchingEffectiveDate("");
    setEditingContribution(null);
  };

  const handleEdit = (contribution) => {
    setEditingContribution(contribution);
    setFormPerson(contribution.person);
    setFormContribution(String(contribution.contribution_per_paycheck));
    setFormFrequency(contribution.paycheck_frequency);
    setFormEffectiveDate(contribution.contribution_effective_date);
    setFormHasMatching(contribution.has_matching);
    setFormMatchingAmount(String(contribution.matching_per_paycheck || ""));
    setFormMatchingEffectiveDate(contribution.matching_effective_date || "");
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Delete this retirement contribution?")) return;
    try {
      await api.delete(`/retirement-contributions/${id}`);
      toast.success("Contribution deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete contribution");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formPerson || !formContribution) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      await api.post("/retirement-contributions", {
        person: formPerson,
        year,
        contribution_per_paycheck: parseFloat(formContribution),
        paycheck_frequency: formFrequency,
        contribution_effective_date: formEffectiveDate,
        has_matching: formHasMatching,
        matching_per_paycheck: formHasMatching ? parseFloat(formMatchingAmount) || 0 : 0,
        matching_effective_date: formHasMatching ? formMatchingEffectiveDate || null : null,
      });
      
      toast.success(editingContribution ? "Contribution updated!" : "Contribution added!");
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error("Failed to save contribution");
    }
  };

  // Check which people already have contributions
  const existingPeople = new Set((summary?.contributions || []).map(c => c.person));
  const availablePeople = ["Rick", "Nazli"].filter(p => !existingPeople.has(p) || editingContribution?.person === p);

  // Calculate per-paycheck totals
  const getPaychecksPerYear = (freq) => freq === "biweekly" ? 26 : 24;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-slide-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-['Outfit'] tracking-tight text-slate-900">
            Retirement (401k)
          </h1>
          <p className="text-slate-500 mt-1">
            Track 401k contributions and employer matching
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          {/* Year Selector */}
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-[100px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {[currentYear - 1, currentYear, currentYear + 1].map(y => (
                <SelectItem key={y} value={String(y)}>{y}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Dialog open={dialogOpen} onOpenChange={(open) => {
            setDialogOpen(open);
            if (!open) resetForm();
          }}>
            {availablePeople.length > 0 && (
              <DialogTrigger asChild>
                <Button className="gap-2 bg-slate-900 hover:bg-slate-800">
                  <Plus className="h-4 w-4" />
                  Add Contribution
                </Button>
              </DialogTrigger>
            )}
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="font-['Outfit']">
                {editingContribution ? "Edit 401k Contribution" : "Add 401k Contribution"}
              </DialogTitle>
              <DialogDescription>
                Set up paycheck contributions and employer matching
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label>Person</Label>
                <Select value={formPerson} onValueChange={setFormPerson} disabled={!!editingContribution}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select person" />
                  </SelectTrigger>
                  <SelectContent>
                    {availablePeople.map(p => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Contribution per Paycheck</Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formContribution}
                      onChange={(e) => setFormContribution(e.target.value)}
                      className="pl-7"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Paycheck Frequency</Label>
                  <Select value={formFrequency} onValueChange={setFormFrequency}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="biweekly">Every 2 weeks (26/yr)</SelectItem>
                      <SelectItem value="semimonthly">2x per month (24/yr)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Contribution Effective Date</Label>
                <Input
                  type="date"
                  value={formEffectiveDate}
                  onChange={(e) => setFormEffectiveDate(e.target.value)}
                />
              </div>

              {/* Annual preview */}
              {formContribution && (
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                  <p className="text-emerald-800">
                    <strong>Annual contribution:</strong>{" "}
                    {formatCurrency(parseFloat(formContribution) * getPaychecksPerYear(formFrequency))}
                    <span className="text-emerald-600 ml-1">
                      ({getPaychecksPerYear(formFrequency)} paychecks × {formatCurrency(parseFloat(formContribution))})
                    </span>
                  </p>
                </div>
              )}

              {/* Employer Matching Section */}
              <div className="border-t pt-4">
                <div className="flex items-center space-x-2 mb-4">
                  <Checkbox
                    id="has-matching"
                    checked={formHasMatching}
                    onCheckedChange={setFormHasMatching}
                  />
                  <Label htmlFor="has-matching" className="cursor-pointer">
                    Employer matching?
                  </Label>
                </div>

                {formHasMatching && (
                  <div className="space-y-4 pl-6 border-l-2 border-emerald-200">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Matching per Paycheck</Label>
                        <div className="relative">
                          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            placeholder="0.00"
                            value={formMatchingAmount}
                            onChange={(e) => setFormMatchingAmount(e.target.value)}
                            className="pl-7"
                          />
                        </div>
                      </div>
                      <div className="space-y-2">
                        <Label>Matching Effective Date</Label>
                        <Input
                          type="date"
                          value={formMatchingEffectiveDate}
                          onChange={(e) => setFormMatchingEffectiveDate(e.target.value)}
                          placeholder="Same as contribution"
                        />
                        <p className="text-xs text-slate-500">Leave blank if same as contribution</p>
                      </div>
                    </div>

                    {formMatchingAmount && (
                      <div className="bg-sky-50 border border-sky-200 rounded-lg p-3 text-sm">
                        <p className="text-sky-800">
                          <strong>Annual matching:</strong>{" "}
                          {formatCurrency(parseFloat(formMatchingAmount) * getPaychecksPerYear(formFrequency))}
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Total preview */}
              {formContribution && (
                <div className="bg-slate-100 rounded-lg p-4 text-center">
                  <p className="text-sm text-slate-600">Total Annual 401k</p>
                  <p className="text-2xl font-bold font-mono text-slate-900">
                    {formatCurrency(
                      (parseFloat(formContribution) || 0) * getPaychecksPerYear(formFrequency) +
                      (formHasMatching ? (parseFloat(formMatchingAmount) || 0) * getPaychecksPerYear(formFrequency) : 0)
                    )}
                  </p>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} className="flex-1">
                  Cancel
                </Button>
                <Button type="submit" className="flex-1 bg-slate-900 hover:bg-slate-800">
                  {editingContribution ? "Update" : "Add"} Contribution
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-emerald-600 font-medium">YTD Contributions</p>
                <p className="text-3xl font-bold font-mono text-emerald-700 mt-1">
                  {formatCurrency(summary?.total_ytd || 0)}
                </p>
                <p className="text-xs text-emerald-600 mt-1">
                  Personal: {formatCurrency(summary?.total_ytd_contribution || 0)} + 
                  Match: {formatCurrency(summary?.total_ytd_matching || 0)}
                </p>
              </div>
              <PiggyBank className="h-10 w-10 text-emerald-400" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-sky-200 bg-gradient-to-br from-sky-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-sky-600 font-medium">Annual Goal</p>
                <p className="text-3xl font-bold font-mono text-sky-700 mt-1">
                  {formatCurrency(summary?.total_annual_goal || 0)}
                </p>
                <p className="text-xs text-sky-600 mt-1">
                  Combined contributions + matching
                </p>
              </div>
              <DollarSign className="h-10 w-10 text-sky-400" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>

        <Card className="border-slate-200">
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-600 font-medium">Contributors</p>
                <p className="text-3xl font-bold font-mono text-slate-700 mt-1">
                  {summary?.contributions?.length || 0}
                </p>
                <p className="text-xs text-slate-500 mt-1">
                  {(summary?.contributions || []).map(c => c.person).join(", ") || "None set up"}
                </p>
              </div>
              <Users className="h-10 w-10 text-slate-400" strokeWidth={1.5} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Individual Contributions */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="font-['Outfit'] text-lg">
            401k Contributions by Person
          </CardTitle>
          <CardDescription>
            Configure contribution amounts and employer matching for each person
          </CardDescription>
        </CardHeader>
        <CardContent>
          {(summary?.contributions || []).length > 0 ? (
            <div className="space-y-4">
              {(summary?.contributions || []).map((contribution) => (
                <div
                  key={contribution.id}
                  className="p-4 bg-slate-50 rounded-lg border border-slate-100"
                >
                  <div className="flex items-start justify-between">
                    <div className="space-y-3 flex-1">
                      <div className="flex items-center gap-3">
                        <span className="text-lg font-semibold text-slate-900">
                          {contribution.person}
                        </span>
                        <Badge variant="secondary" className="bg-slate-200">
                          {contribution.paycheck_frequency === "biweekly" ? "Every 2 weeks" : "2x/month"}
                        </Badge>
                      </div>
                      
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-slate-500">Per Paycheck</p>
                          <p className="font-mono font-semibold text-slate-900">
                            {formatCurrency(contribution.contribution_per_paycheck)}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">YTD Contribution</p>
                          <p className="font-mono font-semibold text-emerald-600">
                            {formatCurrency(contribution.ytd_contribution)}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">
                            {contribution.has_matching ? "YTD Matching" : "Matching"}
                          </p>
                          <p className={`font-mono font-semibold ${contribution.has_matching ? "text-sky-600" : "text-slate-400"}`}>
                            {contribution.has_matching 
                              ? formatCurrency(contribution.ytd_matching)
                              : "None"}
                          </p>
                        </div>
                        <div>
                          <p className="text-slate-500">YTD Total</p>
                          <p className="font-mono font-semibold text-slate-900">
                            {formatCurrency(contribution.ytd_total)}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-4 text-xs text-slate-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Effective: {new Date(contribution.contribution_effective_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                        </span>
                        {contribution.has_matching && contribution.matching_effective_date && (
                          <span className="flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Matching from: {new Date(contribution.matching_effective_date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </span>
                        )}
                        <span>
                          Annual goal: {formatCurrency(contribution.annual_total_goal)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-1 ml-4">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(contribution)}
                      >
                        <Edit2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(contribution.id)}
                        className="text-red-500 hover:text-red-600 hover:bg-red-50"
                      >
                        <Trash2 className="h-4 w-4" strokeWidth={1.5} />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <PiggyBank className="h-12 w-12 text-slate-300 mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-lg font-semibold text-slate-900 mb-1">No 401k contributions set up</h3>
              <p className="text-slate-500 mb-4">Add your 401k contribution details to track retirement savings</p>
              <Button onClick={() => setDialogOpen(true)} className="gap-2">
                <Plus className="h-4 w-4" />
                Add First Contribution
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
